/**
 * Copyright (c) 2026 Finorda Pty Ltd
 * All rights reserved.
 *
 * This file is part of the Finorda platform.
 * Unauthorised copying, modification or
 * distribution of this software is
 * strictly prohibited.
 *
 * Proprietary and confidential.
 * Governed by Queensland law, Australia.
 */

/**
 * @shared-code — Finorda/Standards/shared-code-registry.md #3.6
 * POG Authentication Context
 *
 * Based on canonical AuthContext pattern.
 * Uses /me API call for POG-specific user data (role, factory, distributor)
 * since the Portal JWT doesn't contain POG authorization details.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authService } from '../services/api'

const AuthContext = createContext(null)

const PANELOK_TENANT_CODE = 'PANELOK'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    // Check for token in URL (from Portal SSO redirect)
    const urlParams = new URLSearchParams(window.location.search)
    const urlToken = urlParams.get('token')

    if (urlToken) {
      localStorage.setItem('token', urlToken)
      window.history.replaceState({}, document.title, window.location.pathname)
    }

    const token = localStorage.getItem('token')
    if (!token) {
      setLoading(false)
      return
    }

    try {
      const response = await authService.me()
      setUser(response.user || response)
    } catch (error) {
      console.error('Auth check failed:', error)
      localStorage.removeItem('token')
    } finally {
      setLoading(false)
    }
  }

  const login = useCallback(async (email, password) => {
    const response = await authService.login(email, password)
    localStorage.setItem('token', response.token)
    setUser(response.user)
    return response.user
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('token')
    setUser(null)
  }, [])

  const isPanelok = useCallback(() => {
    if (!user) return false
    if (user.hierarchyLevel === 'global' || user.hierarchy_level === 'global') return true
    if (user.tenantCode === PANELOK_TENANT_CODE || user.tenant_code === PANELOK_TENANT_CODE) return true
    return false
  }, [user])

  const isPanelokAdmin = useCallback(() => {
    if (!user) return false
    const level = user.hierarchyLevel || user.hierarchy_level
    const code = user.tenantCode || user.tenant_code
    if (level === 'global' && user.role === 'admin') return true
    if (code === PANELOK_TENANT_CODE && user.role === 'admin') return true
    return false
  }, [user])

  const isDistributor = useCallback(() => {
    if (!user) return false
    return user.isDistributor || user.is_distributor || false
  }, [user])

  const redirectToPortalLogin = useCallback(() => {
    const portalUrl = import.meta.env.VITE_PORTAL_URL || 'https://portal.panelok.com.au'
    const returnUrl = encodeURIComponent(window.location.href)
    window.location.href = `${portalUrl}/login?redirect=${returnUrl}&app=POG`
  }, [])

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    isPanelok,
    isPanelokAdmin,
    isDistributor,
    redirectToPortalLogin,
    userName: user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() : null,
    tenantId: user?.tenantId || user?.tenant_id,
    tenantCode: user?.tenantCode || user?.tenant_code
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
